import { useState, type KeyboardEvent } from 'react'
import { Box, IconButton, Stack, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { capitalize, formatarBRL } from '../lib/formato';
import { ConectorComplemento } from './conector-complemento';

/** Complemento já normalizado: `valorTotal` é a linha inteira (unitário × qtd). */
export interface SubitemPedido {
  nome: string;
  quantidade: number;
  valorTotal: number;
  /** Adicional de um sabor: sai recuado, logo abaixo do sabor a que pertence. */
  recuo?: boolean
  /** Parte que a opção ocupa num passo fracionado ("1/2"); aparece no lugar do "1×". */
  fracao?: string
}

export interface ItemPedidoListado {
  id: string;
  nome: string;
  quantidade: number;
  /** Total da linha, complementos já somados. */
  valorTotal: number;
  imagemUrl?: string;
  observacao?: string | null;
  subitens?: SubitemPedido[];
  /** Item tem passos e pode ser reaberto para edição. */
  editavel?: boolean;
  avisoFaixa?: string | null;
}

/**
 * O que a tela permite fazer com o item. Cada callback ausente esconde o
 * controle correspondente — é assim que a mesma lista serve tanto o carrinho
 * (mexe em tudo) quanto o resumo da conta (só leitura).
 */
export interface AcoesItemPedido {
  aoAlterarQuantidade?: (id: string, delta: number) => void;
  /** Quantidade digitada, já validada (1 a 999). Sem ela o número entre o − e o + é só texto e o + não tem teto. */
  aoDefinirQuantidade?: (id: string, quantidade: number) => void
  aoDuplicar?: (id: string) => void;
  aoRemover?: (id: string) => void;
  aoEditar?: (id: string) => void;
  aoAlterarObservacao?: (id: string, observacao: string) => void
  /**
   * Trava o "−" em 1. Use onde zerar não é o caminho de remover — no carrinho
   * quem tira o item é a lixeira, não o decremento.
   */
  minimoUm?: boolean;
  /** Bloqueia todos os controles (ex.: enquanto o pedido está sendo enviado). */
  desabilitado?: boolean;
}

type Tamanho = 'normal' | 'grande';

export const LIMITE_OBSERVACAO = 500

const LINHAS_MINIMAS_OBSERVACAO = 2
const LINHAS_MAXIMAS_OBSERVACAO = 4
const LINHAS_VISIVEIS_OBSERVACAO = 2

const RECORTE_OBSERVACAO = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: LINHAS_VISIVEIS_OBSERVACAO,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
} as const

/**
 * Listagem única dos itens de um pedido — carrinho do celular, carrinho do
 * totem/tablet, conferência do que vai ser lançado e resumo da conta.
 *
 * Antes eram quatro desenhos para a mesma informação: dois carrinhos com bloco
 * de texto e complementos em legenda cinza, a conferência do totem de checkout
 * com foto, e a confirmação do pedido listando só "2× Nome" sem complemento
 * nenhum — o cliente conferia o pedido sem ver o que tinha escolhido dentro de
 * cada produto.
 *
 * O desenho adotado é o do totem de checkout: foto, nome, valor da linha e os
 * complementos pendurados num conector, SEMPRE com quantidade e valor (ou "Sem
 * custo"). Os controles entram por `acoes`, e o que a tela não passa não
 * aparece.
 */
export function ListaItensPedido({
  itens,
  acoes = {},
  tamanho = 'normal',
  tecladoProprio = false,
}: {
  itens: ItemPedidoListado[];
  acoes?: AcoesItemPedido;
  tamanho?: Tamanho;
  tecladoProprio?: boolean
}): JSX.Element {
  return (
    <Stack divider={<Box sx={{ borderBottom: '1px dashed', borderColor: 'divider' }} />}>
      {itens.map((item) => (
        <LinhaItemPedido key={item.id} item={item} acoes={acoes} tamanho={tamanho} tecladoProprio={tecladoProprio} />
      ))}
    </Stack>
  );
}

export function LinhaItemPedido({
  item,
  acoes = {},
  tamanho = 'normal',
  tecladoProprio = false,
}: {
  item: ItemPedidoListado;
  acoes?: AcoesItemPedido;
  tamanho?: Tamanho;
  tecladoProprio?: boolean
}): JSX.Element {
  const grande = tamanho === 'grande';
  const { aoAlterarQuantidade, aoDefinirQuantidade, aoDuplicar, aoRemover, aoEditar, aoAlterarObservacao, minimoUm, desabilitado } = acoes

  const [rascunho, setRascunho] = useState<string | null>(null)

  const lado = grande ? 72 : 56;
  const alvo = grande ? 48 : 40;
  const temControles = !!(aoAlterarQuantidade || aoDuplicar || aoRemover || aoEditar || aoAlterarObservacao)
  const subitens = item.subitens ?? [];
  const nome = capitalize(item.nome);

  return (
    <Stack spacing={1} sx={{ width: '100%', py: grande ? 1.5 : 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={grande ? 2 : 1.5} sx={{ width: '100%' }}>
        <Box
          sx={{
            width: lado,
            height: lado,
            flexShrink: 0,
            display: 'flex',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            alignItems: 'center',
            borderColor: 'divider',
            justifyContent: 'center',
            backgroundColor: 'action.hover',
          }}
        >
          {item.imagemUrl ? (
            <Box
              component="img"
              src={item.imagemUrl}
              alt={nome}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: grande ? 30 : 22, opacity: 0.24 }} />
          )}
        </Box>

        <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant={grande ? 'h5' : 'subtitle1'} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {/* Sem controle de quantidade, ela precisa aparecer junto ao nome. */}
            {aoAlterarQuantidade ? nome : `${item.quantidade}× ${nome}`}
          </Typography>

          {rascunho === null && item.observacao && (
            <Typography
              variant={grande ? 'body1' : 'body2'}
              color="text.secondary"
              sx={aoAlterarObservacao ? RECORTE_OBSERVACAO : { overflowWrap: 'anywhere' }}
            >
              Obs: {item.observacao}
            </Typography>
          )}

          <Typography variant={grande ? 'h6' : 'body1'} color="success.main" sx={{ fontWeight: 700 }}>
            {formatarBRL(item.valorTotal)}
          </Typography>

          {item.avisoFaixa && (
            <Typography
              variant={grande ? 'body1' : 'body2'}
              sx={{ color: 'warning.dark', fontWeight: 600 }}
            >
              {item.avisoFaixa}
            </Typography>
          )}
        </Stack>

        {temControles && (
          <Stack
            direction={grande ? 'column' : 'row'}
            alignItems={grande ? 'flex-end' : 'center'}
            spacing={grande ? 1 : 0.25}
            sx={{ flexShrink: 0 }}
          >
            {aoAlterarQuantidade && (
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <IconButton
                  aria-label={`Diminuir ${nome}`}
                  onClick={() => aoAlterarQuantidade(item.id, -1)}
                  disabled={desabilitado || (minimoUm && item.quantidade <= 1)}
                  sx={{ width: alvo, height: alvo }}
                >
                  <RemoveIcon fontSize={grande ? 'medium' : 'small'} />
                </IconButton>

                {aoDefinirQuantidade ? (
                  <CampoQuantidade
                    quantidade={item.quantidade}
                    aoMudar={(quantidade) => aoDefinirQuantidade(item.id, quantidade)}
                    rotulo={`Quantidade de ${nome}`}
                    desabilitado={desabilitado}
                  />
                ) : (
                  <Typography
                    variant={grande ? 'h6' : 'body1'}
                    sx={{ minWidth: grande ? 32 : 24, textAlign: 'center', fontWeight: 700 }}
                  >
                    {item.quantidade}
                  </Typography>
                )}

                <IconButton
                  aria-label={`Aumentar ${nome}`}
                  color="primary"
                  onClick={() => aoAlterarQuantidade(item.id, 1)}
                  disabled={desabilitado || (!!aoDefinirQuantidade && item.quantidade >= QUANTIDADE_MAXIMA)}
                  sx={{ width: alvo, height: alvo }}
                >
                  <AddIcon fontSize={grande ? 'medium' : 'small'} />
                </IconButton>
              </Stack>
            )}

            <Stack direction="row" alignItems="center" spacing={0.25}>
              {aoAlterarObservacao && (
                <IconButton
                  aria-label={item.observacao ? `Editar observação de ${nome}` : `Adicionar observação em ${nome}`}
                  title={item.observacao ? 'Editar observação' : 'Adicionar observação'}
                  onClick={() => setRascunho(item.observacao ?? '')}
                  disabled={desabilitado || rascunho !== null}
                  sx={{ width: alvo, height: alvo }}
                >
                  <EditNoteIcon color="primary" fontSize={grande ? 'medium' : 'small'} />
                </IconButton>
              )}

              {aoEditar && item.editavel && (
                <IconButton
                  aria-label={`Editar ${nome}`}
                  title="Editar item"
                  color="primary"
                  onClick={() => aoEditar(item.id)}
                  disabled={desabilitado}
                  sx={{ width: alvo, height: alvo }}
                >
                  <EditOutlinedIcon fontSize={grande ? 'medium' : 'small'} />
                </IconButton>
              )}

              {aoDuplicar && (
                <IconButton
                  aria-label={`Repetir ${nome}`}
                  title="Repetir item"
                  onClick={() => aoDuplicar(item.id)}
                  disabled={desabilitado}
                  sx={{ width: alvo, height: alvo }}
                >
                  <ContentCopyIcon fontSize={grande ? 'medium' : 'small'} />
                </IconButton>
              )}

              {aoRemover && (
                <IconButton
                  aria-label={`Remover ${nome}`}
                  color="error"
                  onClick={() => aoRemover(item.id)}
                  disabled={desabilitado}
                  sx={{ width: alvo, height: alvo }}
                >
                  <DeleteOutlineIcon fontSize={grande ? 'medium' : 'small'} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>

      {aoAlterarObservacao && rascunho !== null && (
        <ObservacaoDoItem
          item={item}
          nome={nome}
          grande={grande}
          alvo={alvo}
          desabilitado={desabilitado}
          tecladoProprio={tecladoProprio}
          rascunho={rascunho}
          aoMudarRascunho={setRascunho}
          aoAlterar={aoAlterarObservacao}
        />
      )}

      {subitens.length > 0 && (
        <Stack>
          {subitens.map((sub, indice) => (
            <ConectorComplemento
              key={`${sub.nome}-${indice}`}
              sx={sub.recuo ? { '&::after': { width: 36 } } : undefined}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ pl: grande ? (sub.recuo ? 9 : 6.5) : (sub.recuo ? 8 : 5.5), py: 0.5, alignItems: 'flex-end', justifyContent: 'space-between' }}
              >
                <Typography variant={grande ? 'h6' : 'body2'} color="text.secondary">
                  {sub.fracao ?? `${sub.quantidade}×`} {capitalize(sub.nome)}
                </Typography>

                <Typography
                  variant={grande ? 'h6' : 'body2'}
                  color="text.secondary"
                  sx={{ fontWeight: sub.valorTotal > 0 ? 700 : 400, whiteSpace: 'nowrap' }}
                >
                  {sub.valorTotal > 0 ? `+ ${formatarBRL(sub.valorTotal)}` : 'Sem custo'}
                </Typography>
              </Stack>
            </ConectorComplemento>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ObservacaoDoItem({
  item,
  nome,
  grande,
  alvo,
  desabilitado,
  tecladoProprio,
  rascunho,
  aoMudarRascunho,
  aoAlterar,
}: {
  item: ItemPedidoListado
  nome: string
  grande: boolean
  alvo: number
  desabilitado?: boolean
  tecladoProprio: boolean
  rascunho: string
  aoMudarRascunho: (valor: string | null) => void
  aoAlterar: (id: string, observacao: string) => void
}): JSX.Element {
  function confirmar(): void {
    aoAlterar(item.id, rascunho)
    aoMudarRascunho(null)
  }

  return (
    <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
      <TextField
        value={rascunho}
        onChange={(e) => aoMudarRascunho(e.target.value)}
        onFocus={(e) => {
          const fim = e.currentTarget.value.length
          e.currentTarget.setSelectionRange(fim, fim)
        }}
        placeholder="Observação (opcional)"
        helperText={item.quantidade > 1 ? `Vale para as ${item.quantidade} unidades` : undefined}
        disabled={desabilitado}
        autoFocus
        size="small"
        fullWidth
        multiline
        minRows={LINHAS_MINIMAS_OBSERVACAO}
        maxRows={LINHAS_MAXIMAS_OBSERVACAO}
        slotProps={{
          htmlInput: {
            maxLength: LIMITE_OBSERVACAO,
            inputMode: tecladoProprio ? 'none' : undefined,
            'aria-label': `Observação de ${nome}`,
          },
        }}
        sx={{ '& .MuiInputBase-input': { fontSize: grande ? '1rem' : '0.875rem' } }}
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexShrink: 0 }}>
        <IconButton
          aria-label={`Salvar observação de ${nome}`}
          title="Salvar"
          color="success"
          onClick={confirmar}
          disabled={desabilitado}
          sx={{ width: alvo, height: alvo }}
        >
          <CheckIcon fontSize={grande ? 'medium' : 'small'} />
        </IconButton>

        <IconButton
          aria-label={`Cancelar observação de ${nome}`}
          title="Cancelar"
          onClick={() => aoMudarRascunho(null)}
          sx={{ width: alvo, height: alvo }}
        >
          <CloseIcon fontSize={grande ? 'medium' : 'small'} />
        </IconButton>
      </Stack>
    </Stack>
  )
}

/** Teto do campo e do + no cardápio web: cobre a maior encomenda real (500) e barra o zero a mais por engano. */
export const QUANTIDADE_MAXIMA = 999

// Teto todo 9: limitar os dígitos já limita o valor
const TEXTO_QUANTIDADE = new RegExp(`^\\d{0,${String(QUANTIDADE_MAXIMA).length}}$`)

interface CampoQuantidadeProps {
  quantidade: number
  aoMudar: (quantidade: number) => void
  rotulo: string
  desabilitado?: boolean
}

/** Quantidade digitável de 1 a 999; vazio e 0 ficam só no campo, a quantidade nunca vira 0. */
export function CampoQuantidade({ quantidade, aoMudar, rotulo, desabilitado = false }: CampoQuantidadeProps): JSX.Element {
  const [rascunho, setRascunho] = useState<string | null>(null)
  const [conhecida, setConhecida] = useState(quantidade)
  const [antesDeDigitar, setAntesDeDigitar] = useState(quantidade)

  // − e + mudam a quantidade por fora: o rascunho perde o sentido e o número novo passa a ser o de antes de digitar
  if (quantidade !== conhecida) {
    setConhecida(quantidade)
    setAntesDeDigitar(quantidade)
    setRascunho(null)
  }

  const texto = rascunho ?? String(quantidade)

  function digitar(novo: string): void {
    if (!TEXTO_QUANTIDADE.test(novo)) return
    const nova = Number(novo)
    // Apagar "12" passa pelo "1", já aplicado: vazio ou 0 devolve o 12 na hora para o total não mostrar 1
    if (nova < 1) {
      if (antesDeDigitar !== quantidade) {
        setConhecida(antesDeDigitar)
        aoMudar(antesDeDigitar)
      }
      setRascunho(novo)
      return
    }
    setRascunho(null)
    setConhecida(nova)
    if (nova !== quantidade) aoMudar(nova)
  }

  // Sair vazio ou com 0 descarta o rascunho; o número de antes já voltou ao digitar, aqui é só garantia
  function sair(): void {
    if (rascunho === null) return
    setRascunho(null)
    if (antesDeDigitar === quantidade) return
    setConhecida(antesDeDigitar)
    aoMudar(antesDeDigitar)
  }

  return (
    <TextField
      variant="standard"
      // type="number" derruba o teclado de tela do totem (lib/teclado-tela.ts)
      type="text"
      autoComplete="off"
      value={texto}
      disabled={desabilitado}
      onChange={(e) => digitar(e.target.value)}
      onFocus={(e) => {
        setAntesDeDigitar(quantidade)
        e.target.select()
      }}
      onBlur={sair}
      slotProps={{
        htmlInput: {
          'aria-label': rotulo,
          inputMode: 'numeric',
          enterKeyHint: 'done',
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          },
        },
      }}
      sx={{
        '& .MuiInputBase-input': {
          width: `${Math.max(2, texto.length) + 0.5}ch`,
          px: 0,
          textAlign: 'center',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        },
      }}
    />
  )
}
